import { useState, useEffect, useCallback, useRef } from 'react'

// ── Types ──────────────────────────────────────────────────────────────────

export type UpdateStatus =
  | 'idle'
  | 'checking'
  | 'available'
  | 'not-available'
  | 'downloading'
  | 'downloaded'
  | 'error'

export interface UpdateProgress {
  percent: number
  bytesPerSecond: number
  transferred: number
  total: number
}

export interface AutoUpdaterState {
  /** Current status of the update lifecycle */
  status: UpdateStatus
  /** Version string of the available/downloaded update */
  version: string | null
  /** Release notes markdown */
  releaseNotes: string | null
  /** Download progress (only during 'downloading') */
  progress: UpdateProgress | null
  /** Error message (only during 'error') */
  error: string | null
  /** Current app version */
  appVersion: string | null
  /** Whether to show the modal */
  showModal: boolean
}

export interface AutoUpdaterActions {
  /** Trigger a manual check for updates */
  checkForUpdates: () => void
  /** Start downloading the available update */
  downloadUpdate: () => void
  /** Quit the app and install the downloaded update */
  installUpdate: () => void
  /** Dismiss the modal (download continues in background) */
  dismiss: () => void
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useAutoUpdater(): AutoUpdaterState & AutoUpdaterActions {
  const [state, setState] = useState<AutoUpdaterState>({
    status: 'idle',
    version: null,
    releaseNotes: null,
    progress: null,
    error: null,
    appVersion: null,
    showModal: false,
  })

  // Track whether the current check was manual (user-initiated)
  const isManualCheck = useRef(false)

  const api = typeof window !== 'undefined' ? window.electronAPI : undefined

  // Fetch the current app version on mount
  useEffect(() => {
    if (!api) return
    api.getAppVersion().then((v) => {
      setState((s) => ({ ...s, appVersion: v }))
    }).catch(() => { /* ignore — not in Electron */ })
  }, [api])

  // Subscribe to update-status events from the main process
  useEffect(() => {
    if (!api?.onUpdateStatus) return

    const unsubscribe = api.onUpdateStatus((data) => {
      setState((prev) => {
        switch (data.status) {
          case 'checking':
            return {
              ...prev,
              status: 'checking',
              error: null,
              // Only show modal on manual check
              showModal: isManualCheck.current ? true : prev.showModal,
            }

          case 'available':
            return {
              ...prev,
              status: 'available',
              version: data.version ?? null,
              releaseNotes: data.releaseNotes ?? null,
              error: null,
              showModal: true, // Always show when update is available
            }

          case 'not-available':
            return {
              ...prev,
              status: 'not-available',
              version: data.version ?? prev.appVersion,
              error: null,
              // Only show "up to date" modal on manual check
              showModal: isManualCheck.current ? true : prev.showModal,
            }

          case 'downloading':
            return {
              ...prev,
              status: 'downloading',
              progress: data.progress ?? null,
              error: null,
              // Don't force-show modal during download — user might have dismissed it
            }

          case 'downloaded':
            return {
              ...prev,
              status: 'downloaded',
              version: data.version ?? prev.version,
              progress: null,
              error: null,
              showModal: true, // Always show "Restart & Install" prompt
            }

          case 'error':
            return {
              ...prev,
              status: 'error',
              error: data.error ?? 'Unknown error',
              progress: null,
              // Only show error modal on manual check
              showModal: isManualCheck.current ? true : prev.showModal,
            }

          default:
            return prev
        }
      })

      // Reset manual check flag after processing
      if (data.status !== 'checking') {
        isManualCheck.current = false
      }
    })

    return unsubscribe
  }, [api])

  // ── Actions ────────────────────────────────────────────────────────────

  const checkForUpdates = useCallback(() => {
    if (!api) return
    isManualCheck.current = true
    api.checkForUpdates()
  }, [api])

  const downloadUpdate = useCallback(() => {
    if (!api) return
    api.downloadUpdate()
  }, [api])

  const installUpdate = useCallback(() => {
    if (!api) return
    api.installUpdate()
  }, [api])

  const dismiss = useCallback(() => {
    setState((s) => ({ ...s, showModal: false }))
  }, [])

  return {
    ...state,
    checkForUpdates,
    downloadUpdate,
    installUpdate,
    dismiss,
  }
}
